import type { Step as RcStep } from '@sb/remote-control/types';
import { namingExpression } from '@sb/routine-runner/Step/Capture/captureFileName';

import { type ValidationRule } from '../../types';
import { checkCameraEquipmentReference } from '../../util/cameraEquipment';
import { ActionRequiredError, type WarningFinding } from '../../util/errors';
import { validateCodeExpression } from '../../util/validators';

import { CanonicalStepRuleBase } from './base';
import type { CanonicalValidateArgs } from './base';
import { checkCaptureFileName } from './captureConfigChecks';

class CaptureRule extends CanonicalStepRuleBase {
  public constructor() {
    super('steps.Capture', 'Capture');
  }

  protected validate({
    args,
    routine,
    ctx,
    warn,
  }: CanonicalValidateArgs): void {
    const a = args as Extract<RcStep.Arguments, { argumentKind: 'Capture' }>;

    checkCameraEquipmentReference(
      ctx,
      a.cameraEquipmentId,
      'cameraEquipmentId',
    );

    this.checkSaveEquipmentId(a);

    if (!namingExpression(a.saveFilepath)) {
      checkCaptureFileName(a, routine, ctx, 'fileName');

      return;
    }

    this.checkFilepathPngExtension(a);
    warn(this.checkFilepathExpression(a));
  }

  /**
   * Validate save equipment ID.
   * Throws ActionRequiredError if missing.
   *
   * @throws {ActionRequiredError}
   */
  private checkSaveEquipmentId(args: any): void {
    if (!args.saveEquipmentId) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Save equipment ID is required',
        fieldId: 'saveEquipmentId',
      });
    }
  }

  /**
   * Save filepath must resolve to a `.png` file. Constants must literally end
   * in `.png`; JS/Python expressions must end in `.png"` (closing string quote
   * implies the literal must end with `.png`).
   *
   * @throws {ActionRequiredError}
   */
  private checkFilepathPngExtension(args: any): void {
    const fp = args.saveFilepath;
    if (!fp) return;

    const endsWithPng =
      (fp.kind === 'constant' && fp.value.toString().endsWith('.png')) ||
      (fp.kind === 'JavaScript' && fp.expression.endsWith('.png"')) ||
      (fp.kind === 'Python' && fp.expression.endsWith('.png"'));

    if (
      fp.kind === 'constant' ||
      fp.kind === 'JavaScript' ||
      fp.kind === 'Python'
    ) {
      if (!endsWithPng) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Save file path must end with .png',
          fieldId: 'saveFilepath',
        });
      }
    }
  }

  /**
   * Validate the `saveFilepath` expression. Invalid syntax throws; deprecated
   * syntax goes to `warn`.
   *
   * @throws {ActionRequiredError}
   */
  private checkFilepathExpression(
    args: Extract<RcStep.Arguments, { argumentKind: 'Capture' }>,
  ): WarningFinding | undefined {
    return validateCodeExpression(
      args.saveFilepath,
      'saveFilepath',
      'Save filepath',
    );
  }
}

export const CaptureValidation: ValidationRule =
  new CaptureRule().toValidationRule();
